/**
 * FeatureIdeasPage (T35.7.2 Part A) — the Feedback tab shell.
 *
 * This is the live Feedback-tab destination mounted by `App.tsx`.
 *
 * Composes:
 *   - "Report a problem" CTA (features/report-problems) — the affordance that
 *     used to live in the account sheet (☰), now homed here on the Feedback tab.
 *   - QuickQuestionPrompt (features/quick-questions)
 *   - FeatureIdeasSection (features/feature-ideas)
 *   - DonationBanner (features/subscriptions) — Feedback-tab only by mount
 *
 * No shared feedback-corpus dependency.
 *
 * Ambient: nothing here interrupts or nags. Token-only (CP5); mobile-first; ≥44px targets.
 *
 * Layout fidelity: the page mirrors the canonical prototype's Feedback body
 * (`#s-feedback` in `prototypes/shopista-app/screens/feedback-dark.html`). The
 * proto does NOT use a uniform flex gap — it spaces with per-element margins:
 * intro `.sub` (12px below) → report-cta `.menugroup` (8px below) → `.fb-sectionhead`
 * (22px above). So the page is a plain block and each child carries its own margin.
 */

import { type CSSProperties } from 'react';
import { QuickQuestionPrompt } from '../quick-questions/QuickQuestionPrompt';
import { FeatureIdeasSection } from './FeatureIdeasSection';
import { DonationBanner } from '../subscriptions/DonationBanner';
import { openReportProblemSheet } from '../report-problems';
import { Icon } from '../../components';

// The proto rhythm is per-element margins, not a container gap, so the page
// itself is a plain block (no flex gap).
const pageStyle: CSSProperties = {
  display: 'block',
};

// Intro line — the proto's `.sub`: small, muted, tight above / 12px below.
const subStyle: CSSProperties = {
  margin: '0 0 var(--space-3)', // proto `.sub` margin-bottom 12px
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-muted)',
};

// Report-cta wrapper — the proto's `.menugroup` (margin-bottom 8px). Holds the
// single filled CTA row.
const reportGroupStyle: CSSProperties = {
  margin: '0 0 var(--space-2)', // proto `.menugroup` margin-bottom 8px
};

// The "Feature ideas" block sits ~22px below the report CTA in the proto
// (`.fb-sectionhead` margin-top 22px). The section component itself carries no
// top margin, so the page supplies the gap. 24px (`--space-6`) is the nearest
// token to the proto's 22px.
const ideasSectionWrapStyle: CSSProperties = {
  marginTop: 'var(--space-6)',
};

// Filled-brand "Report a problem" CTA — recreates the proto's
// `.menuitem.report-cta` (the same filled row that used to live in the account
// sheet ☰, recovered from its deleted `reportCtaRowStyle`). Horizontal row:
// leading bubble icon · two-line text · trailing chevron. Brand fill + inverse
// text are the token pair that flips correctly in BOTH themes — in dark,
// `--color-accent-primary` is the light violet and `--color-text-inverse` is a
// dark text, so the row reads as dark-on-violet (matching the proto's hand-forced
// dark-mode override) without any per-theme literal. Hover (deepen to
// accent-secondary) + the keyboard focus ring come from responsive.css, keyed on
// the `feedback-report-cta` testid.
const reportCtaStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-3)',
  width: '100%',
  minHeight: '2.75rem', // ≥44px touch target
  padding: 'var(--space-3q) var(--space-3)', // proto `.report-cta` padding 14px 13px
  backgroundColor: 'var(--color-accent-primary)', // proto `--brand`
  color: 'var(--color-text-inverse)', // proto inverse-on-brand text
  border: 'none',
  borderRadius: 'var(--radius-lg)', // proto `--r-lg` (8px)
  boxShadow: 'var(--shadow-sm)', // proto `--sh-sm`
  fontFamily: 'var(--font-family-sans)',
  cursor: 'pointer',
  textAlign: 'left',
};

// Leading icon slot — proto `.mi-ic` is a 24px centered slot. The icon inherits
// the inverse text color via `currentColor`.
const reportCtaIconStyle: CSSProperties = {
  width: '1.5rem', // 24px slot (proto `.mi-ic` width)
  fontSize: 'var(--font-size-md)',
  display: 'flex',
  justifyContent: 'center',
  flex: '0 0 auto',
};

// Two-line text block — proto `.mi-tx` (flex:1, can ellipsize).
const reportCtaTextStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
};

// Title — proto `.mi-t`: 15px / 600.
const reportCtaTitleStyle: CSSProperties = {
  display: 'block',
  fontSize: 'var(--font-size-base15)', // proto 15px
  fontWeight: 'var(--font-weight-semibold)',
  lineHeight: 'var(--line-height-tight)',
};

// Sub-line — proto `.mi-d`: 12px, the lightened inverse (proto uses
// rgba(255,255,255,.85) on the fill; the token-only equivalent is the inverse
// text at reduced opacity, which flips with the theme).
const reportCtaSubStyle: CSSProperties = {
  display: 'block',
  fontSize: 'var(--font-size-xs)', // proto 12px
  lineHeight: 'var(--line-height-tight)',
  marginTop: 'var(--space-1)',
  opacity: 0.85,
};

// Trailing chevron — proto `.mi-cv`, inherits the inverse color.
const reportCtaChevStyle: CSSProperties = {
  display: 'flex',
  flex: '0 0 auto',
};

export function FeatureIdeasPage(): JSX.Element {
  return (
    <div style={pageStyle} data-testid="feedback-page">
      <p style={subStyle}>
        What&apos;s broken, what you wish it did — help shape what&apos;s next.
      </p>

      {/* "Report a problem" CTA — the affordance users knew from the account
          menu (☰), which no longer carries it. Triggers the global ReportSheet
          mounted in AppShell via the report-problems event opener. Filled-brand
          row matching the proto `.menuitem.report-cta`. */}
      <div style={reportGroupStyle}>
        <button
          type="button"
          data-testid="feedback-report-cta"
          style={reportCtaStyle}
          onClick={() => openReportProblemSheet({ screenContext: 'Feedback tab' })}
        >
          <span style={reportCtaIconStyle} aria-hidden="true">
            <Icon name="chat" />
          </span>
          <span style={reportCtaTextStyle}>
            <span style={reportCtaTitleStyle}>Report a problem</span>
            <span style={reportCtaSubStyle}>Tell us what isn&apos;t working.</span>
          </span>
          <span style={reportCtaChevStyle} aria-hidden="true">
            <Icon name="chevron-right" />
          </span>
        </button>
      </div>

      {/* The open quick question (T35.4) — ambient, dismissible, on-screen only.
          Renders nothing when there is no eligible question (the common case) or
          in executing/Shopping mode. */}
      <div data-testid="feedback-question-slot">
        <QuickQuestionPrompt />
      </div>

      {/* Feature ideas (T35.7.1) — published-only cards to upvote + the propose
          sheet. Suppressed in executing (Shopping) mode by the section itself.
          The ~22px gap above (proto `.fb-sectionhead` margin-top) is page rhythm,
          supplied here so the section's own internals stay untouched. */}
      <div style={ideasSectionWrapStyle}>
        <FeatureIdeasSection />
      </div>

      {/* The pinned "support development" banner (T35.7.2) — Feedback-tab only by
          virtue of being mounted here; pinned above the nav; session-dismissible. */}
      <DonationBanner />
    </div>
  );
}
